// Add a VST sensor (POST /devices): DevEUI, name, the account that owns it,
// and the type's onboarding settings (GET /types/{id}/onboarding, e.g. the
// VST use case, whose change factory-resets the sensor) when the tenant has
// them switched on.
import { useState } from "preact/hooks";
import { REGIMES, classKeys, clsName } from "../data/classes";
import { useT } from "../i18n";
import { tenantApi, tree } from "../lib/account";
import { displayName } from "../lib/account";
import { useLoad } from "../lib/load";
import { fleet } from "../data/fleet";
import { errText } from "../lib/apierr";

const slug = (x: string) => x.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const TYPE = "ch.gimasi.vst";
interface OnbKey { key: string; label: string; description?: string; type: string; enum?: { name: string; value: number | string; label?: string }[]; default?: unknown; confirm?: string }

export function AddSensor({ onDone }: { onDone: (id: string) => void }) {
  const t = useT();
  const onb = useLoad(() => tenantApi<{ enabled: boolean; keys: OnbKey[]; confirm: Record<string, string> }>("GET", `/types/${TYPE}/onboarding?lang=${t.lang}`), [t.lang]);
  const [f, setF] = useState({ device_serial: "", name: "", regime: "", city: "", area: "", site: "", account_id: tree.value[0]?.a.id || "" });
  const [cfg, setCfg] = useState<Record<string, unknown>>({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const keys = onb.data?.enabled ? onb.data.keys : [];
  const set = (k: string) => (e: Event) => setF({ ...f, [k]: (e.target as HTMLInputElement).value });
  const send = async (e: Event) => {
    e.preventDefault();
    setBusy(true);
    setMsg("");
    const config = Object.fromEntries(keys.map((k) => [k.key, cfg[k.key] ?? k.default]).filter(([, v]) => v !== undefined));
    try {
      const r = await tenantApi<{ device_id: string }>("POST", "/devices", {
        device_serial: f.device_serial.trim().toLowerCase(), name: f.name || undefined, type_id: TYPE, account_id: f.account_id || undefined,
        attributes: { ...(f.regime ? { cold_class: f.regime, temperature_regime: REGIMES[f.regime]?.regime } : {}), ...(f.site ? { country: "CH", city: slug(f.city), area: slug(f.area), site: slug(f.site) } : {}) },
        ...(keys.length ? { config } : {}),
      });
      onDone(r.device_id);
    } catch (err) {
      setMsg(errText(t, err));
    }
    setBusy(false);
  };
  return (
    <section class="card">
      <form class="row" style="flex-wrap:wrap;gap:10px;align-items:flex-end" onSubmit={send}>
        <label>DevEUI<br /><input required pattern="[0-9A-Fa-f]{16}" title={t("x.devEuiHint")} class="mono" value={f.device_serial} onInput={set("device_serial")} /></label>
        <label>{t("x.name")}<br /><input value={f.name} placeholder="FI-03" onInput={set("name")} /></label>
        <label>{t("x.account")}<br /><select value={f.account_id} onChange={set("account_id")}>
          {tree.value.map(({ a, depth }) => <option key={a.id} value={a.id}>{"  ".repeat(depth)}{depth === 0 ? displayName(a) + " · HQ" : a.name}</option>)}
        </select></label>
        <label>{t("x.regime")}<br /><select value={f.regime} onChange={set("regime")}>
          <option value="">{t("x.lc.noRegime")}</option>{classKeys().map((r) => <option key={r} value={r}>{clsName(t.lang, r)}</option>)}
        </select></label>
        {(["city", "area", "site"] as const).map((k) => (
          <label key={k}>{t("lvl." + k)}<br /><input list={"add-" + k} value={f[k]} onInput={set(k)} /><datalist id={"add-" + k}>{[...new Set((fleet.value?.points || []).map((p) => String(p.labels[k] || "")).filter(Boolean))].map((v) => <option key={v} value={v} />)}</datalist></label>
        ))}
        {keys.map((k) => (
          <label key={k.key} title={k.description || ""}>{k.label}<br />
            {k.enum
              ? <select value={String(cfg[k.key] ?? k.default ?? "")} onChange={(e) => setCfg({ ...cfg, [k.key]: (e.target as HTMLSelectElement).value })}>
                  {k.enum.map((o) => <option key={o.name} value={o.name}>{o.name} · {o.label || o.name}</option>)}
                </select>
              : <input value={String(cfg[k.key] ?? k.default ?? "")} onInput={(e) => setCfg({ ...cfg, [k.key]: (e.target as HTMLInputElement).value })} />}
          </label>
        ))}
        <button class="btn" type="submit" disabled={busy}>{t("x.addSensor")}</button>
      </form>
      {keys.filter((k) => onb.data?.confirm?.[k.key]).map((k) => <p key={k.key} class="st st-warn" style="margin:10px 0 0">{onb.data!.confirm[k.key]}</p>)}
      {msg && <p style="margin:10px 0 0">{msg}</p>}
    </section>
  );
}
